import { AnonymousAvatar } from "@/components/ui/AnonymousAvatar";
import { MaskIcon } from "@/components/ui/MaskIcon";
import VehiclePlate from "@/components/ui/VehiclePlate";
import { WebActionSheet } from "@/components/ui/WebActionSheet";
import { useAuth } from "@/context/AuthContext";
import { usePosts } from "@/context/PostsContext";
import {
  useFeedUnmuted,
  useFeedVideosSuspended,
  useVideoVisibility,
} from "@/context/VideoVisibilityContext";
import { firestore } from "@/firebase/web";
import { useAutoplayAllowed } from "@/hooks/useAutoplayAllowed";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader.web";
import { useLikeAction } from "@/hooks/useLikeAction.web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Post } from "@/types/post";
import { formatCount } from "@/utils/formatCount";
import { useFormatDateTime, useFormatTimeAgo } from "@/utils/formatDate";
import { isVideoUri } from "@/utils/isVideoUri";
import { cachePost } from "@/utils/postCache";
import { REPORT_REASON_KEYS } from "@/utils/reportReasons";
import { getHostname, isDomainAllowed } from "@/utils/url";
import { Ionicons } from "@expo/vector-icons";
import Feather from "@expo/vector-icons/Feather";
import { Circle, GoogleMap, Marker } from "@react-google-maps/api";
import { requestBrowserPosition } from "@/utils/geo.web";
import { router } from "expo-router";
import {
  addDoc,
  deleteDoc,
  doc,
  collection as firestoreCollection,
  serverTimestamp,
} from "firebase/firestore";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { EventTypeBadge } from "./EventTypeBadge";
import { PostDescription } from "./PostDescription";
import { ThemedText } from "./ThemedText";
import { ThemedView } from "./ThemedView";
import { AuthorName } from "./ui/AuthorName";
import { AvatarImage } from "./ui/AvatarImage";
import { Button } from "./ui/Button";
import CustomModal from "./ui/CustomModal.web";
import { IconSymbol } from "./ui/IconSymbol";

const MediaSkeleton = () => (
  <div
    className="animate-pulse"
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "#e5e7eb",
      borderRadius: 8,
    }}
  />
);

const MediaError = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onRetry();
      }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 8,
        backgroundColor: "#f3f4f6",
        border: "none",
        borderRadius: 8,
        cursor: "pointer",
        padding: 16,
      }}
    >
      <Ionicons name="cloud-offline-outline" size={36} color="#6b7280" />
      <ThemedText style={{ color: "#374151", textAlign: "center" }}>
        {t("FAILED_TO_LOAD_MEDIA")}
      </ThemedText>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          color: "#2563eb",
        }}
      >
        <Ionicons name="refresh" size={16} color="#2563eb" />
        <ThemedText style={{ color: "#2563eb" }}>
          {t("TAP_TO_RETRY")}
        </ThemedText>
      </div>
    </button>
  );
};

const MediaItem = ({
  uri,
  style,
  cover,
  mediaId,
  isLCPCandidate,
  alt,
}: {
  uri: string;
  style?: any;
  cover?: boolean;
  mediaId?: string;
  /** True for the first paint-eligible image on the screen. Drives fetchpriority/loading hints so the LCP image races to the front of the request queue. */
  isLCPCandidate?: boolean;
  alt?: string;
}) => {
  const { t } = useTranslation();
  const isVideo = isVideoUri(uri);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const autoplayAllowed = useAutoplayAllowed();
  const isActive = useVideoVisibility(mediaId ?? "", containerRef);
  const [unmuted, setUnmuted] = useFeedUnmuted();
  const shouldAutoplay = isVideo && !!mediaId && autoplayAllowed && isActive;

  const handleError = () => {
    setErrored(true);
    setLoaded(false);
  };

  const handleRetry = () => {
    setErrored(false);
    setLoaded(false);
    setRetryCount((c) => c + 1);
  };

  useEffect(() => {
    if (!isVideo) return;
    const video = videoRef.current;
    if (!video || errored) return;
    if (shouldAutoplay) {
      if (video.paused) {
        // Autoplay play() can reject for benign reasons (AbortError when a
        // pause races a pending play, NotAllowedError on locked-down browsers).
        // These are not load failures — let onError signal real failures.
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } else if (!video.paused) {
      video.pause();
    }
  }, [shouldAutoplay, isVideo, errored, loaded, retryCount]);

  if (isVideo) {
    return (
      <div
        ref={containerRef}
        style={{
          ...style,
          position: "relative",
          height: cover ? "100%" : undefined,
          minHeight: cover ? undefined : loaded ? undefined : 200,
        }}
      >
        {!loaded && !errored && <MediaSkeleton />}
        {!errored && (
          <video
            ref={videoRef}
            key={retryCount}
            src={uri}
            style={{
              width: "100%",
              height: cover ? "100%" : "auto",
              maxHeight: cover ? "none" : "70vh",
              objectFit: cover ? "cover" : "contain",
              borderRadius: cover ? 0 : 8,
              backgroundColor: "#f0f0f0",
              display: "block",
            }}
            loop
            muted={!unmuted}
            playsInline
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onLoadedData={() => setLoaded(true)}
            onError={handleError}
          />
        )}
        {errored && <MediaError onRetry={handleRetry} />}
        {loaded && !isPlaying && !shouldAutoplay && !errored && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.15)",
              borderRadius: 8,
              display: "flex",
            }}
          >
            <Ionicons
              name="play-circle"
              size={64}
              color="#fff"
              style={{ opacity: 0.85 }}
            />
          </div>
        )}
        {loaded && !errored && (
          <button
            aria-label={unmuted ? t("MUTE_VIDEO") : t("UNMUTE_VIDEO")}
            aria-pressed={!unmuted}
            onClick={(e) => {
              e.stopPropagation();
              setUnmuted(!unmuted);
            }}
            style={{
              position: "absolute",
              bottom: 12,
              right: 12,
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: "rgba(0,0,0,0.5)",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              zIndex: 2,
            }}
          >
            <Ionicons
              name={unmuted ? "volume-high" : "volume-mute"}
              size={20}
              color="#fff"
            />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: cover ? "100%" : undefined,
        // Reserve a 4:3 box for single images until the real image lands. Removes
        // the 0→natural-height layout shift; the carousel wrapper already pins
        // its own aspect-ratio so we don't override it when cover is true.
        aspectRatio: !cover && !loaded ? "4 / 3" : undefined,
        minHeight: cover || loaded ? undefined : 200,
      }}
    >
      {!loaded && !errored && <MediaSkeleton />}
      {!errored && (
        <img
          key={retryCount}
          src={uri}
          alt={alt ?? t("POST_IMAGE_ALT")}
          loading={isLCPCandidate ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={isLCPCandidate ? "high" : "auto"}
          style={{
            ...styles.postImage,
            ...style,
            ...(cover && {
              height: "100%",
              maxHeight: "none",
              objectFit: "cover",
              marginBottom: 0,
              borderRadius: 0,
            }),
            display: "block",
            opacity: loaded ? 1 : 0,
          }}
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
      {errored && <MediaError onRetry={handleRetry} />}
    </div>
  );
};

type PostCardProps = {
  post: Post;
  /** True when this card is the first in the feed viewport on a cold render. Propagated to media so the first image becomes the LCP candidate. */
  isLCPCandidate?: boolean;
  /** Fires after a successful delete. Post-details uses it to navigate away
   * before the still-mounted comment listeners hit `permission-denied` on the
   * now-missing parent post. */
  onDeleted?: () => void;
};

const PostCardComponent = ({
  post,
  isLCPCandidate,
  onDeleted,
}: PostCardProps) => {
  const { t } = useTranslation();
  const { user, userData } = useAuth();
  const canEdit = !!userData?.capabilities?.canEditPost;
  const { formatDateTime } = useFormatDateTime();
  const [isReportModalVisible, setReportModalVisible] = useState(false);
  const [isActionSheetVisible, setActionSheetVisible] = useState(false);
  const [isShareSheetVisible, setShareSheetVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({ visible: false, title: "", message: "" });
  const [confirmConfig, setConfirmConfig] = useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ visible: false, title: "", message: "", onConfirm: () => {} });

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenData, setFullScreenData] = useState<{
    uris: string[];
    index: number;
  }>({
    uris: [],
    index: 0,
  });
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const openFullScreen = (uris: string[], index: number) => {
    setFullScreenData({ uris, index });
    setIsFullScreen(true);
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
  };

  const [, setFeedVideosSuspended] = useFeedVideosSuspended();
  useEffect(() => {
    if (!isFullScreen) return;
    setFeedVideosSuspended(true);
    return () => setFeedVideosSuspended(false);
  }, [isFullScreen, setFeedVideosSuspended]);

  const handleSliderScroll = () => {
    const el = sliderRef.current;
    if (!el || el.clientWidth === 0) return;
    setActiveSlide(Math.round(el.scrollLeft / el.clientWidth));
  };

  const scrollToSlide = (index: number) => {
    const el = sliderRef.current;
    if (!el || typeof el.scrollTo !== "function") return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  };

  const iconColor = useThemeColor({}, "icon");
  const stripesColor = useThemeColor({}, "stripes");

  const { liked, likesCount, toggleLike } = useLikeAction(post, user?.uid);
  const [commentsCount, setCommentsCount] = useState(post.commentCount || 0);

  const { setPosts } = usePosts();

  // commentsCount mirrors the same prop-anchor pattern the hook uses for likes,
  // but the comment action lives on the post-detail screen, so there's no
  // optimistic update path to encapsulate — keeping it inline.
  useEffect(() => {
    setCommentsCount(post.commentCount || 0);
  }, [post.commentCount]);

  const handleLike = () => {
    if (!user?.uid) {
      router.push("/(auth)/login");
      return;
    }
    void toggleLike();
  };

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/post/${post.id}`;
  const hasNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: post.author.displayName,
        text: post.description,
        url: shareUrl,
      });
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") return;
      console.error(t("ERROR"), error);
      setAlertConfig({
        visible: true,
        title: t("ERROR"),
        message: t("COULD_NOT_SHARE"),
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setAlertConfig({
        visible: true,
        title: t("SUCCESS"),
        message: t("LINK_COPIED"),
      });
    } catch (error) {
      console.error(t("ERROR"), error);
      setAlertConfig({
        visible: true,
        title: t("ERROR"),
        message: t("COULD_NOT_SHARE"),
      });
    }
  };

  const handleReportSubmit = async (reason: string, details: string) => {
    if (!user?.uid) {
      router.push("/(auth)/login");
      return;
    }
    try {
      await addDoc(firestoreCollection(firestore, "reports"), {
        postId: post.id,
        reportedBy: user.uid,
        reason,
        details,
        createdAt: serverTimestamp(),
      });
      setAlertConfig({
        visible: true,
        title: t("SUCCESS"),
        message: t("THANK_YOU_FOR_YOUR_FEEDBACK"),
      });
    } catch (error) {
      console.error(t("ERROR"), error);
      setAlertConfig({
        visible: true,
        title: t("ERROR"),
        message: t("COULD_NOT_SUBMIT_REPORT"),
      });
    } finally {
      setReportModalVisible(false);
    }
  };

  const handleDelete = async () => {
    setConfirmConfig({
      visible: true,
      title: t("DELETE_POST"),
      message: t("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_POST"),
      onConfirm: async () => {
        try {
          await deleteDoc(doc(firestore, "posts", post.id));
          setPosts((prevPosts) => prevPosts.filter((p) => p.id !== post.id));
          onDeleted?.();
        } catch (error) {
          console.error(t("ERROR"), error);
          setAlertConfig({
            visible: true,
            title: t("ERROR"),
            message: t("COULD_NOT_DELETE_POST"),
          });
        }
      },
    });
  };

  const showActionSheet = () => {
    setActionSheetVisible(true);
  };

  const goToPostDetails = () => {
    // Hand this exact Post to PostDetailsScreen via the in-memory cache so
    // it can paint on the same frame as the route transition.
    cachePost(post);
    router.push({
      pathname: "/(tabs)/post/[postId]",
      params: { postId: post.id },
    });
  };

  const openFullScreenOnKey =
    (uris: string[], index: number) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        openFullScreen(uris, index);
      }
    };

  const hasMultipleImages = (post.images?.length || 0) > 1;

  // Firestore has no runtime schema enforcement; a doc where `author` exists
  // but is missing `uid` / `displayName` (partial write, legacy) would crash
  // the render below. PostCardGuarded only checks `!post.author`, so this
  // catches the shaped-but-invalid author case that slips past it.
  if (!post?.author?.uid) return null;

  const isAnonymous = !!post.anonymous;

  return (
    <ThemedView
      style={{
        ...styles.card,
        borderColor: stripesColor,
        borderBottomWidth: 3,
        ...(isReportModalVisible && { position: "relative", zIndex: 1 }),
      }}
    >
      <ReportModal
        visible={isReportModalVisible}
        onClose={() => setReportModalVisible(false)}
        onSubmit={handleReportSubmit}
      />
      {(() => {
        const isAuthor = user?.uid === post.author.uid;
        // Options are built conditionally so the index-to-action mapping
        // stays correct no matter which combination is present. An admin
        // editing their own post sees Edit + Delete; on others' posts
        // they see Edit + Report; non-admins keep the original layout.
        const options: string[] = [];
        if (canEdit) options.push(t("EDIT"));
        if (isAuthor) options.push(t("DELETE"));
        if (!isAuthor) options.push(t("REPORT"));
        options.push(t("CANCEL"));

        const editIndex = canEdit ? 0 : -1;
        const deleteIndex = isAuthor ? (canEdit ? 1 : 0) : -1;
        const reportIndex = !isAuthor ? (canEdit ? 1 : 0) : -1;
        const cancelIndex = options.length - 1;

        return (
          <WebActionSheet
            visible={isActionSheetVisible}
            onClose={() => setActionSheetVisible(false)}
            options={options}
            destructiveButtonIndex={
              deleteIndex >= 0 ? deleteIndex : undefined
            }
            cancelButtonIndex={cancelIndex}
            onSelect={(index) => {
              if (index === editIndex) {
                router.push({
                  // typed-routes generation needs the file at
                  // app/(tabs)/post/edit/[postId].tsx; cast until section 6
                  // adds it so builds don't regress.
                  pathname: "/(tabs)/post/edit/[postId]" as any,
                  params: { postId: post.id },
                });
              } else if (index === deleteIndex) {
                handleDelete();
              } else if (index === reportIndex) {
                setReportModalVisible(true);
              }
            }}
          />
        );
      })()}
      <WebActionSheet
        visible={isShareSheetVisible}
        onClose={() => setShareSheetVisible(false)}
        options={
          hasNativeShare
            ? [t("SHARE"), t("COPY_LINK"), t("CANCEL")]
            : [t("COPY_LINK"), t("CANCEL")]
        }
        cancelButtonIndex={hasNativeShare ? 2 : 1}
        onSelect={(index) => {
          if (hasNativeShare) {
            if (index === 0) handleNativeShare();
            else if (index === 1) handleCopyLink();
          } else if (index === 0) {
            handleCopyLink();
          }
        }}
      />

      <CustomModal
        visible={alertConfig.visible}
        onClose={() => setAlertConfig((prev) => ({ ...prev, visible: false }))}
        title={alertConfig.title}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ThemedText>{alertConfig.message}</ThemedText>
          <Button
            text={t("OK")}
            onPress={() =>
              setAlertConfig((prev) => ({ ...prev, visible: false }))
            }
          />
        </div>
      </CustomModal>

      <CustomModal
        visible={confirmConfig.visible}
        onClose={() =>
          setConfirmConfig((prev) => ({ ...prev, visible: false }))
        }
        title={confirmConfig.title}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ThemedText>{confirmConfig.message}</ThemedText>
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              text={t("CANCEL")}
              onPress={() =>
                setConfirmConfig((prev) => ({ ...prev, visible: false }))
              }
              style={{ flex: 1, backgroundColor: "#ccc" }}
            />
            <Button
              text={t("CONFIRM")}
              onPress={() => {
                confirmConfig.onConfirm();
                setConfirmConfig((prev) => ({ ...prev, visible: false }));
              }}
              style={{ flex: 1, backgroundColor: "red" }}
            />
          </div>
        </div>
      </CustomModal>
      <div
        onClick={goToPostDetails}
        style={{ display: "flex", flexDirection: "column", cursor: "pointer" }}
      >
        <div style={styles.header}>
          {isAnonymous ? (
            <AnonymousAvatar size={40} style={styles.avatar} />
          ) : (
            <AvatarImage
              photoURL={post.author?.photoURL}
              style={styles.avatar}
              alt={post.author.displayName ?? post.author.username ?? "Avatar"}
              userId={post.author.uid}
            />
          )}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {isAnonymous ? (
                <div
                  data-testid="anonymous-author"
                  style={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  <AuthorName
                    displayName={undefined}
                    username={undefined}
                    isVerified={post.author.isVerified}
                  />
                  <MaskIcon
                    size={14}
                    color={iconColor}
                    style={{ marginLeft: 6 }}
                  />
                </div>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push({
                      pathname: "/(tabs)/public-profile",
                      params: { userId: post.author.uid },
                    });
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    display: "flex",
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <AuthorName
                      displayName={post.author.displayName}
                      username={post.author.username}
                      isVerified={post.author.isVerified}
                    />
                  </div>
                </button>
              )}
              <LocationWithTimeAgo post={post} />
            </div>
            <button
              aria-label={t("MORE_OPTIONS")}
              onClick={(e) => {
                e.stopPropagation();
                showActionSheet();
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <IconSymbol
                name="ellipsis.vertical"
                size={24}
                color={iconColor}
              />
            </button>
          </div>
        </div>
        <div style={styles.dateTypeSection}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Feather name="calendar" size={16} color="#666" />
            <ThemedText style={{ ...styles.date, marginLeft: 4 }}>
              {formatDateTime(post.eventDate)}
            </ThemedText>
          </div>
          {post.eventType && (
            <EventTypeBadge
              eventType={post.eventType}
              style={{ marginBottom: 4 }}
            />
          )}
        </div>
        <PostDescription text={post.description} />
        {post.sourceUrl && isDomainAllowed(post.sourceUrl) && (
          <a
            href={post.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={t("POST_SOURCE_LINK_A11Y", {
              hostname: getHostname(post.sourceUrl),
              snippet: (post.description || post.id)
                .trim()
                .replace(/\s+/g, " ")
                .slice(0, 80),
            })}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 13,
              color: "#666",
              textDecoration: "none",
              marginTop: 6,
              marginBottom: 4,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Feather name="external-link" size={14} color="#666" />
            <span
              style={{
                textDecoration: "underline",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t("POST_SOURCE_PREFIX")} {getHostname(post.sourceUrl)}
            </span>
          </a>
        )}
        {post.images && post.images.length > 0 ? (
          <>
            <div
              ref={sliderRef}
              onScroll={handleSliderScroll}
              className="post-media-slider"
              style={{
                display: "flex",
                flexDirection: "row",
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                ...(hasMultipleImages && {
                  alignItems: "flex-start",
                  marginBottom: 8,
                }),
              }}
            >
              {post.images.map((uri, index) => (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  aria-label={t("VIEW_MEDIA")}
                  onClick={(e) => {
                    e.stopPropagation();
                    openFullScreen(post.images || [], index);
                  }}
                  onKeyDown={openFullScreenOnKey(post.images || [], index)}
                  style={{
                    cursor: "pointer",
                    flex: "0 0 100%",
                    scrollSnapAlign: "center",
                    ...(hasMultipleImages && {
                      aspectRatio: CAROUSEL_ASPECT_RATIO,
                      overflow: "hidden",
                      borderRadius: 8,
                      backgroundColor: "black",
                    }),
                  }}
                >
                  <MediaItem
                    uri={uri}
                    cover={hasMultipleImages}
                    mediaId={`${post.id}:${index}`}
                    isLCPCandidate={isLCPCandidate && index === 0}
                    style={{
                      width: "100%",
                      background: "black",
                    }}
                  />
                </div>
              ))}
            </div>
            {hasMultipleImages && (
              <div style={styles.previewDotsRow}>
                {post.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollToSlide(i);
                    }}
                    aria-label={t("GO_TO_IMAGE_N", { n: i + 1 })}
                    aria-current={i === activeSlide}
                    style={{
                      ...styles.previewDot,
                      ...(i === activeSlide && {
                        backgroundColor: iconColor,
                        width: 8,
                        height: 8,
                      }),
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : post.image ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={t("VIEW_MEDIA")}
            onClick={(e) => {
              e.stopPropagation();
              openFullScreen([post.image!], 0);
            }}
            onKeyDown={openFullScreenOnKey([post.image], 0)}
            style={{ cursor: "pointer" }}
          >
            <MediaItem
              uri={post.image}
              mediaId={post.id}
              isLCPCandidate={isLCPCandidate}
            />
          </div>
        ) : null}
        {(post.vehiclePlates?.length || 0) > 0 && (
          <div>
            <ThemedText type="defaultSemiBold">{t("PLATES")}: </ThemedText>
            <div
              style={{ display: "flex", marginBottom: 8, overflowX: "auto" }}
            >
              {post.vehiclePlates
                ?.filter((plate) => plate && plate.plate && plate.country)
                .map((plate) => (
                  <VehiclePlate
                    key={`${plate.country}.${plate.plate}`}
                    plate={plate.plate}
                    country={plate.country}
                  />
                ))}
            </div>
          </div>
        )}

        <div style={styles.actions}>
          <button
            aria-label={liked ? t("UNLIKE") : t("LIKE")}
            aria-pressed={liked}
            onClick={(e) => {
              e.stopPropagation();
              handleLike();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 56,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <Ionicons
              name={liked ? "heart" : "heart-outline"}
              size={22}
              color={liked ? "#e74c3c" : iconColor}
            />
            <ThemedText style={{ minWidth: 32, textAlign: "left" }}>
              {formatCount(likesCount)}
            </ThemedText>
          </button>
          <button
            aria-label={t("COMMENTS")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 56,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              goToPostDetails();
            }}
          >
            <Ionicons name="chatbubble-outline" size={22} color={iconColor} />
            <ThemedText style={{ minWidth: 32, textAlign: "left" }}>
              {formatCount(commentsCount)}
            </ThemedText>
          </button>
          <button
            aria-label={t("SHARE")}
            onClick={(e) => {
              e.stopPropagation();
              setShareSheetVisible(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              marginLeft: "auto",
            }}
          >
            <Ionicons name="share-social-outline" size={22} color={iconColor} />
          </button>
        </div>
      </div>
      <FullScreenMediaModal
        visible={isFullScreen}
        uris={fullScreenData.uris}
        startIndex={fullScreenData.index}
        onClose={closeFullScreen}
      />
    </ThemedView>
  );
};

// Reference equality on `post` is enough because PostsContext reuses existing
// post object references on append / silent refresh (the Map-based dedupe
// keeps the prior reference for ids that already exist). A wholesale refetch
// produces fresh references and correctly re-renders every card.
const arePostCardPropsEqual = (
  prev: PostCardProps,
  next: PostCardProps,
): boolean =>
  prev.post === next.post && prev.isLCPCandidate === next.isLCPCandidate;

// Skip render when the post doc is missing required fields (malformed Firestore
// doc). Kept in a wrapper so PostCardComponent's hook order stays stable — an
// early return inside PostCardComponent would violate Rules of Hooks if the
// same slot ever swapped a valid post for a malformed one.
const PostCardGuarded = (props: PostCardProps) => {
  if (!props.post?.id || !props.post?.author) return null;
  return <PostCardComponent {...props} />;
};

export const PostCard = memo(PostCardGuarded, arePostCardPropsEqual);

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

// Multi-image carousels lock every slide to this ratio so the card height
// stays constant while swiping; single-media posts keep their natural ratio.
const CAROUSEL_ASPECT_RATIO = "4 / 3";

const ZoomableImage = ({ uri, alt }: { uri: string; alt?: string }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // Mirror state into refs so the (once-bound) wheel listener and pointer
  // handlers always read current values without stale closures.
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const translateRef = useRef(translate);
  translateRef.current = translate;

  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const panRef = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
  } | null>(null);
  const [interacting, setInteracting] = useState(false);

  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [uri]);

  const clampScale = (s: number) => Math.min(Math.max(s, MIN_ZOOM), MAX_ZOOM);

  const clampTranslate = (tx: number, ty: number, s: number) => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return { x: tx, y: ty };
    const maxX = Math.max(0, (img.offsetWidth * s - container.clientWidth) / 2);
    const maxY = Math.max(
      0,
      (img.offsetHeight * s - container.clientHeight) / 2,
    );
    return {
      x: Math.min(Math.max(tx, -maxX), maxX),
      y: Math.min(Math.max(ty, -maxY), maxY),
    };
  };

  const zoomAt = (clientX: number, clientY: number, nextScaleRaw: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const s = scaleRef.current;
    const ns = clampScale(nextScaleRaw);
    const t = translateRef.current;
    const relX = clientX - cx;
    const relY = clientY - cy;
    const ntx = relX - (ns / s) * (relX - t.x);
    const nty = relY - (ns / s) * (relY - t.y);
    setScale(ns);
    setTranslate(clampTranslate(ntx, nty, ns));
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAt(e.clientX, e.clientY, scaleRef.current * factor);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e: React.PointerEvent) => {
    // iOS Safari throws InvalidStateError DOMException when the pointer has
    // already been torn down mid-dispatch. Non-fatal but noisy.
    try {
      containerRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // ignore, capture is a nice-to-have, gestures still work without it.
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setInteracting(true);
    if (pointers.current.size === 2) {
      const [p1, p2] = [...pointers.current.values()];
      pinchRef.current = { dist: dist(p1, p2), scale: scaleRef.current };
      panRef.current = null;
    } else if (pointers.current.size === 1 && scaleRef.current > 1) {
      panRef.current = {
        x: e.clientX,
        y: e.clientY,
        tx: translateRef.current.x,
        ty: translateRef.current.y,
      };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchRef.current) {
      if (pinchRef.current.dist === 0) return;
      const [p1, p2] = [...pointers.current.values()];
      const ns =
        pinchRef.current.scale * (dist(p1, p2) / pinchRef.current.dist);
      zoomAt((p1.x + p2.x) / 2, (p1.y + p2.y) / 2, ns);
    } else if (pointers.current.size === 1 && panRef.current) {
      const dx = e.clientX - panRef.current.x;
      const dy = e.clientY - panRef.current.y;
      setTranslate(
        clampTranslate(
          panRef.current.tx + dx,
          panRef.current.ty + dy,
          scaleRef.current,
        ),
      );
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      pinchRef.current = null;
      if (pointers.current.size === 1 && scaleRef.current > 1) {
        const [p] = [...pointers.current.values()];
        panRef.current = {
          x: p.x,
          y: p.y,
          tx: translateRef.current.x,
          ty: translateRef.current.y,
        };
      }
    }
    if (pointers.current.size === 0) {
      panRef.current = null;
      setInteracting(false);
      if (scaleRef.current <= 1.001) setTranslate({ x: 0, y: 0 });
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (scaleRef.current > 1) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    } else {
      zoomAt(e.clientX, e.clientY, 2.5);
    }
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        touchAction: "none",
        cursor: scale > 1 ? "grab" : "zoom-in",
      }}
    >
      <img
        ref={imgRef}
        src={uri}
        alt={alt ?? t("POST_IMAGE_ALT")}
        draggable={false}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: interacting ? "none" : "transform 0.12s ease-out",
          userSelect: "none",
          WebkitUserSelect: "none",
          willChange: "transform",
        }}
      />
    </div>
  );
};

const FullScreenMediaModal = ({
  visible,
  uris,
  startIndex,
  onClose,
}: {
  visible: boolean;
  uris: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (visible) {
      setCurrentIndex(startIndex);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [visible, startIndex]);

  if (!visible || !uris || uris.length === 0) {
    return null;
  }

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % uris.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + uris.length) % uris.length);
  };

  const uri = uris[currentIndex];
  if (!uri) return null;

  const isVideo = isVideoUri(uri);

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "black",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        zIndex: 1000,
      }}
    >
      <button style={styles.fullScreenCloseButton} onClick={onClose}>
        <Ionicons name="close" size={32} color="white" />
      </button>
      {isVideo ? (
        <video
          ref={videoRef}
          src={uri}
          style={{ width: "100%", height: "80%", objectFit: "contain" }}
          controls
          autoPlay
          loop
        />
      ) : (
        <ZoomableImage uri={uri} />
      )}
      {uris.length > 1 && (
        <>
          <button
            style={{ ...styles.arrow, ...styles.leftArrow }}
            onClick={handlePrev}
          >
            <Ionicons name="chevron-back" size={40} color="white" />
          </button>
          <button
            style={{ ...styles.arrow, ...styles.rightArrow }}
            onClick={handleNext}
          >
            <Ionicons name="chevron-forward" size={40} color="white" />
          </button>
          <div style={styles.dotsRow}>
            {uris.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                style={{
                  ...styles.dot,
                  ...(i === currentIndex ? styles.dotActive : null),
                }}
                aria-label={t("GO_TO_ITEM_N", { n: i + 1 })}
              />
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
};

const ReportModal = ({
  visible,
  onClose,
  onSubmit,
}: {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: string, details: string) => void;
}) => {
  const { t } = useTranslation();
  const [reasonKey, setReasonKey] = useState<string>("");
  const [details, setDetails] = useState("");

  if (!visible) return null;

  return createPortal(
    <div style={styles.modalContainer}>
      <div style={styles.modalContent}>
        <ThemedText style={styles.modalTitle}>{t("REPORT_POST")}</ThemedText>
        <ThemedView>
          {REPORT_REASON_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => setReasonKey(key)}
              style={{
                ...styles.reason,
                ...(reasonKey === key ? styles.selectedReason : {}),
                background: "none",
                border: "none",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              {t(key)}
            </button>
          ))}
        </ThemedView>
        <textarea
          style={styles.detailsInput}
          placeholder={t("OPTIONAL_DETAILS")}
          value={details}
          disabled={reasonKey !== "OTHER"}
          onChange={(e) => setDetails(e.target.value)}
        />
        <Button
          onPress={() => onSubmit(t(reasonKey), details)}
          disabled={!reasonKey}
          text={t("SUBMIT_REPORT")}
        />
        <Button
          text={t("CANCEL")}
          onPress={onClose}
          style={{ backgroundColor: "red", width: "100%", marginTop: 10 }}
        />
      </div>
    </div>,
    document.body,
  );
};

const MY_LOCATION_BLUE = "#1a73e8";

function LocationWithTimeAgo({ post }: { post: Post }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [myPos, setMyPos] = useState<{ lat: number; lng: number } | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const { t } = useTranslation();
  const { formatTimeAgo } = useFormatTimeAgo();
  const { isLoaded, loadError } = useGoogleMapsLoader();

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (modalVisible) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [modalVisible]);

  const refreshMyPos = useCallback(async () => {
    const result = await requestBrowserPosition();
    if (result.kind !== "ok") {
      setMyPos(null);
      return null;
    }
    const next = { lat: result.position.lat, lng: result.position.lng };
    setMyPos(next);
    return next;
  }, []);

  useEffect(() => {
    if (!modalVisible) return;
    void refreshMyPos();
  }, [modalVisible, refreshMyPos]);

  const handleRecenter = useCallback(async () => {
    const next = (await refreshMyPos()) ?? myPos;
    if (next && mapRef.current) {
      mapRef.current.panTo(next);
    }
  }, [refreshMyPos, myPos]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const city = post.location?.city || "";
  const country = post.location?.country || "";
  const hasCoords = !!(
    post.location?.geoPoint &&
    typeof post.location.geoPoint.latitude === "number" &&
    typeof post.location.geoPoint.longitude === "number"
  );
  const latitude: number =
    hasCoords && typeof post.location?.geoPoint?.latitude === "number"
      ? post.location.geoPoint.latitude
      : 0;
  const longitude: number =
    hasCoords && typeof post.location?.geoPoint?.longitude === "number"
      ? post.location.geoPoint.longitude
      : 0;
  const locationString =
    city && country ? `${city}, ${country}` : city || country || "";
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
        <ThemedText style={[styles.date, { color: "#767676" }]}>
          {formatTimeAgo(post.date)}
        </ThemedText>
        <ThemedText style={styles.date}> · </ThemedText>
        {locationString ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (hasCoords) setModalVisible(true);
            }}
            disabled={!hasCoords}
            style={{
              display: "flex",
              alignItems: "center",
              background: "none",
              border: "none",
              cursor: hasCoords ? "pointer" : "default",
              minWidth: 0,
              maxWidth: "60%",
              padding: 0,
            }}
          >
            <IconSymbol name="location" size={15} color="#007BFF" />
            <ThemedText
              style={
                {
                  textDecorationLine: "underline",
                  color: "#007BFF",
                  fontSize: 14,
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                } as any
              }
            >
              {locationString}
            </ThemedText>
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            <IconSymbol name="location.slash" size={15} color="#767676" />
            <ThemedText style={[styles.date, { color: "#767676" }]}>
              {t("NO_LOCATION")}
            </ThemedText>
          </div>
        )}
        {post.plateFollowersOnly ? (
          <>
            <ThemedText style={styles.date}> · </ThemedText>
            <IconSymbol name="padlock" size={14} color={"#666"} />
          </>
        ) : null}
      </div>
      {modalVisible &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#fff",
              zIndex: 1000,
            }}
          >
            {loadError ? (
              <div style={{ padding: 20, textAlign: "center" }}>
                {t("MAP_ERROR")}
              </div>
            ) : isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={{ lat: latitude, lng: longitude }}
                zoom={15}
                onLoad={handleMapLoad}
                onUnmount={handleMapUnmount}
                options={{
                  scrollwheel: true,
                  gestureHandling: "greedy",
                  zoomControl: false,
                  streetViewControl: false,
                  cameraControl: false,
                }}
              >
                {hasCoords && (
                  <Marker position={{ lat: latitude, lng: longitude }} />
                )}
                {myPos && (
                  <>
                    <Circle
                      center={myPos}
                      radius={30}
                      options={{
                        fillColor: MY_LOCATION_BLUE,
                        fillOpacity: 0.15,
                        strokeOpacity: 0,
                        clickable: false,
                        zIndex: 1,
                      }}
                    />
                    <Circle
                      center={myPos}
                      radius={6}
                      options={{
                        fillColor: MY_LOCATION_BLUE,
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeOpacity: 1,
                        strokeWeight: 2,
                        clickable: false,
                        zIndex: 2,
                      }}
                    />
                  </>
                )}
              </GoogleMap>
            ) : (
              <div>{t("MAP_LOADING")}</div>
            )}
            {isLoaded && myPos && (
              <button
                type="button"
                aria-label={t("MY_LOCATION")}
                data-testid="recenter-button"
                onClick={handleRecenter}
                style={{
                  position: "absolute",
                  bottom: 80,
                  right: 10,
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  border: "none",
                  background: "#ffffff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                <IconSymbol
                  name="location.fill"
                  size={22}
                  color={MY_LOCATION_BLUE}
                />
              </button>
            )}
            <Button
              text={t("CLOSE_MAP")}
              style={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                width: "calc(100% - 40px)" as any,
              }}
              onPress={() => setModalVisible(false)}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

const styles = {
  modalContainer: {
    position: "fixed" as "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    zIndex: 1000,
  },
  date: {
    fontSize: 14,
    color: "#666",
  },
  header: {
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    marginBottom: 8,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 10,
  },
  dateTypeSection: {
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "space-between",
    marginBottom: 8,
    borderBottom: "1px solid #eee",
  },
  card: {
    padding: 16,
  },
  postImage: {
    width: "100%",
    height: "auto",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: "#f0f0f0",
  },
  description: {
    paddingTop: 8,
    paddingBottom: 8,
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700" as "700",
    marginBottom: 10,
  },
  actions: {
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "flex-start",
    borderTop: "1px solid #eee",
    paddingTop: 8,
  },
  reason: {
    fontSize: 16,
    paddingTop: 10,
    paddingBottom: 10,
  },
  modalContent: {
    width: "80%",
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
  },
  selectedReason: {
    fontWeight: "700" as "700",
    color: "#007BFF",
  },
  detailsInput: {
    border: "1px solid #ccc",
    borderRadius: 5,
    padding: 10,
    marginTop: 10,
    marginBottom: 20,
    width: "calc(100% - 20px)",
    boxSizing: "border-box" as "border-box",
  },
  fullScreenCloseButton: {
    position: "absolute" as "absolute",
    top: 50,
    right: 20,
    zIndex: 1,
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  arrow: {
    position: "absolute" as "absolute",
    top: "50%",
    marginTop: -22,
    backgroundColor: "rgba(0,0,0,0.3)",
    borderRadius: 32,
    zIndex: 2,
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  leftArrow: {
    left: 5,
  },
  rightArrow: {
    right: 5,
  },
  dotsRow: {
    position: "absolute" as "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    zIndex: 2,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.4)",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
  dotActive: {
    backgroundColor: "rgba(255,255,255,1)",
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  previewDotsRow: {
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  previewDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: "rgba(127,127,127,0.4)",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
};
